import { cdn_url } from '@shared/cdn';

export const ErrorHandler = {};

/** Caught errors are placed in this array until the error sender is loaded,
 * when this array is converted into an object that implements a push method.
 * The errors loaded in the array are then sent.
 */
ErrorHandler.caughtErrors = [];

/** Errors are reported via image requests. The associated image objects are
 * stored here as it may be possible that a browser will stop an image request
 * if the image object is immediately discarded.
 */
ErrorHandler.errorImages = [];

/**
 * The number of errors that have occurred on the page.
 */
ErrorHandler.numberOfErrors = 0;

/**
 * Where the reports of uncaught errors are sent.
 */
ErrorHandler.uncaughtReportingSrc = '/err/e.gif';

/**
 * Where the reports of caught errors are sent.
 */
ErrorHandler.caughtReportingSrc = '/err/f.gif';

/**
 * Where the CaughtErrorSender object is.
 */
ErrorHandler.caughtErrorSenderPath = '/build/js/err/caught-error-sender.js';

/**
 * Errors including this message are ignored by
 * reportUncaught. Used in throwReportedError to
 * throw an error that has already been reported
 * without being reported a second time by
 * reportUncaught.
 */
ErrorHandler.reportedErrorMessagePrefix = 'Reported to ErrorHandler: ';

/**
 * The method to be used to report errors. For example this may be used as in
 * ErrorHandler.report("string!") or ErrorHandler.report(error) or
 * ErrorHandler.report(error, "string!")
 *
 * @param {object} error A string or error object.
 * @param {string} message Optional text.
 */
ErrorHandler.report = function (error, message) {
  try {
    if (++ErrorHandler.numberOfErrors > 20) {
      return;
    }
    message = typeof message === 'undefined' ? null : message;
    let stacktraceError = null;
    if (typeof error === 'string') {
      stacktraceError = new Error('create stacktrace');
    }
    ErrorHandler.caughtErrors.push([error, ErrorHandler.numberOfErrors,
      arguments.callee, message, stacktraceError]);
    if (typeof ErrorHandler.caughtErrors.loaded === 'undefined') {
      ErrorHandler.loadErrorSender();
    }
  } catch (newerr) {
    try {
      // console.log('Error in ErrorHandler.report()');
      // console.log(newerr);
    } catch (newerr2) {
      // Do nothing
    }
  }
};

/**
 * Used by the report function to load the error sender.
 */
ErrorHandler.loadErrorSender = () => {
  try {
    const e = document.createElement('script');
    e.type = 'text/javascript';
    try {
      e.src = cdn_url(ErrorHandler.caughtErrorSenderPath);
    } catch (cdnerr) {
      const protocol = document.location.protocol == 'https:'
        ? 'https'
        : 'http';
      e.src =
        `${protocol}://www.freelancer.com${ErrorHandler.caughtErrorSenderPath}`;
    }
    const s = document.getElementsByTagName('script')[0];
    s.parentNode.insertBefore(e, s);
  } catch (err) {
    // Do nothing
  }
};

/**
 * Can be used to report errors related to jQuery event objects.
 *
 * @param {object} error A string or error object.
 * @param {object} event A jQuery event.
 */
ErrorHandler.reportJQueryEvent = (error, event) => {
  let message = event.type;
  try {
    let node = event.target;
    for (let i = 0; i < 3; i++) {
      message += ` in [${node.outerHTML.substr(0, 50)}]`;
      node = node.parentNode;
    }
  } catch (err2) { return; }
  ErrorHandler.report(error, message);
};

/**
 * Throw a caught error so that it will not be reported by the reportUncaught
 * method.
 *
 * @param {object} err A string or error object.
 */
ErrorHandler.throwReportedError = (err) => {
  if (typeof err === 'object') {
    if (typeof err.description === 'string') {
      err.description = ErrorHandler.reportedErrorMessagePrefix +
              err.description;
    } else if (typeof err.message === 'string') {
      err.message = ErrorHandler.reportedErrorMessagePrefix +
              err.message;
    }
  } else if (typeof err === 'string') {
    err = ErrorHandler.reportedErrorMessagePrefix + err;
  }
  throw err;
};

/**
 * Used to report uncaught errors.
 *
 * @param {string} message The error message from the browser.
 * @param {string} src The file that the browser blames for the error.
 * @param {number} line The line in the file (src) where the error has occurred.
 * @return {boolean} returns Returns false so that the browser still shows
 * errors in the console.
 */
ErrorHandler.reportUncaught = (message, src, line) => {
  try {
    if (message.indexOf(ErrorHandler.reportedErrorMessagePrefix) != -1) {
      return false;
    }
  } catch (err1) { return; }

  try {
    if (++ErrorHandler.numberOfErrors > 20) {
      return false;
    }
    let additional = `?pg=${encodeURIComponent(window.location.href)}`;
    additional += `&n=${ErrorHandler.numberOfErrors}`;
    if (typeof message === 'string') {
      additional += `&msg=${encodeURIComponent(message)}`;
    }
    if (typeof src === 'string') {
      additional += `&src=${encodeURIComponent(src)}`;
    }
    if (typeof line === 'string' || typeof line === 'number') {
      additional += `&ln=${encodeURIComponent(line)}`;
    }
    const img = new Image();
    img.src = ErrorHandler.uncaughtReportingSrc + additional;
    ErrorHandler.errorImages.push(img);
  } catch (err2) {
    try {
      const img = new Image();
      img.src = `${ErrorHandler.uncaughtReportingSrc}` +
              '?err=error%20reporting%20error';
      ErrorHandler.errorImages.push(img);
    } catch (err3) { return; }
  }
  return false;
};

/**
 * Just google window.onerror for more info.
 */
window.onerror = ErrorHandler.reportUncaught;
